/*
 * Furever Active - article revenue + CTA attribution (storefront snippet, v2)
 *
 * What it does:
 *   C1) On a /blogs/<blog>/<article> page, if no first-touch cookie exists yet, it records the first
 *       article this visitor read (slug + blog + timestamp) in a 90-day cookie. First touch only: it
 *       never overwrites an existing cookie, so the first article always gets the credit.
 *   C2) On any page where a cart with items exists, it mirrors that first-touch into Shopify cart
 *       attributes via /cart/update.js. Shopify carries cart attributes onto the order as
 *       note_attributes, which is how the server join (src/lib/revenue-attribution) later maps the
 *       order back to the article that earned it. AJAX add-to-cart is observed so the stamp is not
 *       lost when checkout starts without a page reload.
 *   C3) On article pages, NeuroChew links are labelled by placement and publish a
 *       fa_article_product_click event to Shopify analytics plus the storefront Google tag/data
 *       layer when analytics consent permits it.
 *   C4) The five measured buyer-intent articles get intent-matched, claim-safe CTA copy. The copy
 *       is presentation-only; stored article bodies and search metadata are never rewritten.
 *
 * No PII: only article handles, blog handles, CTA placement, destination path, and timestamps.
 *
 * Contract source of truth: src/lib/revenue-attribution/cookie.ts. The names and 90-day window below
 * are duplicated here because a theme asset cannot import TypeScript. If you change one, change both
 * (tests/revenue-attribution.test.ts guards that they agree).
 */
/* eslint-disable @typescript-eslint/no-unused-vars -- ES5 catch clauses must bind a parameter; pre-ES2019 theme browsers cannot parse `catch {}`. */
(function () {
  "use strict";

  var COOKIE_NAME = "fa_first_touch";
  var MAX_AGE_DAYS = 90;
  var CLICK_EVENT_NAME = "fa_article_product_click";
  var ATTR = {
    slug: "fa_first_touch_slug",
    blog: "fa_first_touch_blog",
    at: "fa_first_touch_at"
  };

  var ROI_COPY = {
    "anxiety-comparison-best-calming-treats-for-dogs-vet-recommended": {
      heading: "Comparing calming aids? Read NeuroChew's full label",
      body: "NeuroChew is our non-sedating daily brain-support chew, not a rescue calming treat or medicine. See every active amount, serving guidance, and the 60-day guarantee before deciding whether it belongs beside your veterinarian's plan.",
      button: "Compare the NeuroChew label"
    },
    "calming-comparison-zesty-paws-calming-bites-for-dogs": {
      heading: "Comparing calming aids? Read NeuroChew's full label",
      body: "NeuroChew is our non-sedating daily brain-support chew, not a rescue calming treat or medicine. See every active amount, serving guidance, and the 60-day guarantee before deciding whether it belongs beside your veterinarian's plan.",
      button: "Compare the NeuroChew label"
    },
    "naturvet-quiet-moments-calming-chews": {
      heading: "Comparing calming aids? Read NeuroChew's full label",
      body: "NeuroChew is our non-sedating daily brain-support chew, not a rescue calming treat or medicine. See every active amount, serving guidance, and the 60-day guarantee before deciding whether it belongs beside your veterinarian's plan.",
      button: "Compare the NeuroChew label"
    },
    "adaptil-calming-collar-for-dogs": {
      heading: "Comparing calming aids? Read NeuroChew's full label",
      body: "NeuroChew is our non-sedating daily brain-support chew, not a rescue calming treat or medicine. See every active amount, serving guidance, and the 60-day guarantee before deciding whether it belongs beside your veterinarian's plan.",
      button: "Compare the NeuroChew label"
    },
    "supplements-for-dog-dementia": {
      heading: "Compare NeuroChew's brain-support ingredients",
      body: "NeuroChew is our daily brain-support chew, not a treatment for dementia. See every active amount, serving guidance, and the 60-day guarantee before deciding with your veterinarian whether it fits your dog's routine.",
      button: "See ingredients and serving amounts"
    }
  };

  function readCookie(name) {
    var target = name + "=";
    var parts = (document.cookie || "").split(";");
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      if (part.indexOf(target) === 0) return part.substring(target.length);
    }
    return null;
  }

  function writeCookie(name, value, days) {
    var expires = new Date(Date.now() + days * 864e5).toUTCString();
    var secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = name + "=" + value + "; Path=/; Max-Age=" + days * 864e5 / 1e3 +
      "; Expires=" + expires + "; SameSite=Lax" + secure;
  }

  // Mirrors parseArticlePath() in cookie.ts: /blogs/<blog>/<article-handle>.
  function parseArticlePath(pathname) {
    var m = /^\/blogs\/([^/]+)\/([^/?#]+)/.exec(pathname);
    if (!m) return null;
    var blog = safeDecode(m[1]);
    var slug = safeDecode(m[2]);
    if (!blog || !slug) return null;
    return { blog: blog, slug: slug };
  }

  function safeDecode(value) {
    try { return decodeURIComponent(value); } catch (e) { return value; }
  }

  function parseFirstTouch(raw) {
    if (!raw) return null;
    var decoded = raw;
    try { decoded = decodeURIComponent(raw); } catch (e) { /* value was not encoded */ }
    var obj;
    try { obj = JSON.parse(decoded); } catch (e) { return null; }
    if (!obj || typeof obj !== "object" || !obj.slug || !obj.blog) return null;
    return obj;
  }

  function hasClass(element, className) {
    var names = element && typeof element.className === "string" ? element.className.split(/\s+/) : [];
    for (var i = 0; i < names.length; i++) if (names[i] === className) return true;
    return false;
  }

  function hasAncestorClass(element, className) {
    var current = element;
    while (current && current !== document) {
      if (hasClass(current, className)) return true;
      current = current.parentNode;
    }
    return false;
  }

  function ctaPlacement(link) {
    if (hasAncestorClass(link, "article-v2__promo")) return "top_promo";
    if (hasAncestorClass(link, "article-v2__product")) return "mid_article_card";
    if (hasAncestorClass(link, "article-v2__endcta")) return "end_article_cta";
    if (hasAncestorClass(link, "article-v2__sticky")) return "mobile_sticky";
    if (hasAncestorClass(link, "article-v2__body")) return "article_body";
    return "article_other";
  }

  function cleanDestination(raw) {
    if (!raw) return "";
    var value = String(raw).split("#")[0].split("?")[0];
    var protocol = value.indexOf("://");
    if (protocol !== -1) {
      var firstSlash = value.indexOf("/", protocol + 3);
      value = firstSlash === -1 ? "/" : value.substring(firstSlash);
    }
    return value;
  }

  function trackedDestination(raw, article, placement) {
    if (!raw || /[?&]fa_article=/.test(raw)) return raw;
    var parts = String(raw).split("#");
    var base = parts.shift() || "";
    var hash = parts.length ? "#" + parts.join("#") : "";
    var separator = base.indexOf("?") === -1 ? "?" : "&";
    return base + separator + "fa_article=" + encodeURIComponent(article.slug) +
      "&fa_cta=" + encodeURIComponent(placement) + hash;
  }

  function analyticsAllowed() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === "function") {
        return window.Shopify.customerPrivacy.analyticsProcessingAllowed() === true;
      }
    } catch (e) { return false; }
    return true;
  }

  function publishProductClick(article, link, placement) {
    if (!analyticsAllowed()) return;
    var payload = {
      article_slug: article.slug,
      article_blog: article.blog,
      cta_placement: placement,
      destination_path: cleanDestination(link.getAttribute("href"))
    };

    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === "function") {
        window.Shopify.analytics.publish(CLICK_EVENT_NAME, payload);
      }
    } catch (e) { /* analytics must never break navigation */ }

    try {
      if (typeof window.gtag === "function") {
        window.gtag("event", CLICK_EVENT_NAME, payload);
      } else if (window.dataLayer && typeof window.dataLayer.push === "function") {
        var dataLayerPayload = { event: CLICK_EVENT_NAME };
        for (var key in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, key)) dataLayerPayload[key] = payload[key];
        }
        window.dataLayer.push(dataLayerPayload);
      }
    } catch (e) { /* analytics must never break navigation */ }
  }

  function bindArticleCtas(article) {
    if (!document.querySelectorAll) return;
    var links = document.querySelectorAll('article a[href*="/pages/neurochew"]');
    for (var i = 0; i < links.length; i++) {
      (function (link) {
        var placement = ctaPlacement(link);
        link.setAttribute("data-far-cta", placement);
        link.addEventListener("click", function () {
          // Add non-UTM params only at click time. The destination page_view becomes measurable in
          // GA4 without exposing crawler-visible parameter links or corrupting acquisition source.
          link.setAttribute("href", trackedDestination(link.getAttribute("href"), article, placement));
          publishProductClick(article, link, placement);
        });
      })(links[i]);
    }
  }

  function replaceCardCopy(root, copy) {
    if (!root || !root.querySelector) return;
    var heading = root.querySelector("h2") || root.querySelector("h3");
    if (heading) heading.textContent = copy.heading;
    var paragraphs = root.querySelectorAll ? root.querySelectorAll("p") : [];
    for (var i = 0; i < paragraphs.length; i++) {
      if (!hasClass(paragraphs[i], "article-v2__product-kicker")) {
        paragraphs[i].textContent = copy.body;
        break;
      }
    }
    var button = root.querySelector('a[href*="/pages/neurochew"]');
    if (button) button.textContent = copy.button;
  }

  function personalizeBuyerIntentCtas(article) {
    var copy = ROI_COPY[article.slug];
    if (!copy || !document.querySelector) return;
    replaceCardCopy(document.querySelector(".article-v2__product-body"), copy);
    replaceCardCopy(document.querySelector(".article-v2__endcta"), copy);

    var sticky = document.querySelector(".article-v2__sticky");
    if (sticky && sticky.querySelector) {
      var stickyTitle = sticky.querySelector(".article-v2__sticky-title");
      var stickyButton = sticky.querySelector('a[href*="/pages/neurochew"]');
      if (stickyTitle) stickyTitle.textContent = copy.button;
      if (stickyButton) stickyButton.textContent = copy.button;
    }
  }

  var currentArticle = parseArticlePath(location.pathname);
  var firstTouch = parseFirstTouch(readCookie(COOKIE_NAME));

  // C1: set the cookie only if absent AND we are on an article page.
  if (!firstTouch && currentArticle) {
    firstTouch = { slug: currentArticle.slug, blog: currentArticle.blog, ts: new Date().toISOString() };
    writeCookie(COOKIE_NAME, encodeURIComponent(JSON.stringify(firstTouch)), MAX_AGE_DAYS);
  }

  if (currentArticle) {
    personalizeBuyerIntentCtas(currentArticle);
    bindArticleCtas(currentArticle);
  }

  // C2: mirror into cart attributes when a cart with items exists and is not already stamped.
  var mirrorInFlight = false;
  if (firstTouch && firstTouch.slug) {
    mirrorToCart(firstTouch);
    observeAjaxCartAdds(firstTouch);
  }

  function mirrorToCart(ft) {
    if (mirrorInFlight) return;
    mirrorInFlight = true;
    window.fetch("/cart.js", { headers: { Accept: "application/json" }, credentials: "same-origin" })
      .then(function (r) { return r && r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || !cart.item_count) return null;       // never create a cart out of nothing
        var attrs = cart.attributes || {};
        if (attrs[ATTR.slug]) return null;                // first touch already stamped; leave it
        var body = { attributes: {} };
        body.attributes[ATTR.slug] = ft.slug;
        body.attributes[ATTR.blog] = ft.blog;
        body.attributes[ATTR.at] = ft.ts || "";
        return window.fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(body)
        });
      })
      .then(function () { mirrorInFlight = false; }, function () { mirrorInFlight = false; });
  }

  function cartAddRequest(input) {
    var raw = typeof input === "string" ? input : input && typeof input.url === "string" ? input.url : "";
    return /\/cart\/add(?:\.js)?(?:[?#]|$)/.test(raw);
  }

  function observeAjaxCartAdds(ft) {
    if (typeof window.fetch === "function" && !window.fetch.__faArticleAttribution) {
      var originalFetch = window.fetch;
      var observedFetch = function () {
        var args = arguments;
        var response = originalFetch.apply(window, args);
        if (cartAddRequest(args[0]) && response && typeof response.then === "function") {
          response.then(function (result) {
            if (result && result.ok) setTimeout(function () { mirrorToCart(ft); }, 0);
          }, function () { /* the storefront owns add-to-cart error handling */ });
        }
        return response;
      };
      observedFetch.__faArticleAttribution = true;
      window.fetch = observedFetch;
    }

    if (document.addEventListener) {
      document.addEventListener("cart:updated", function () { mirrorToCart(ft); });
      document.addEventListener("cart:refresh", function () { mirrorToCart(ft); });
    }
  }
})();
